import { type ReactNode } from 'react'
import NewsSkeleton from '@/components/NewsSkeleton'
import MastheadLayoutSkeleton from '@/components/skeletons/MastheadLayoutSkeleton'
import { MASTHEAD_LAYOUT, type MastheadLayoutId } from '@/lib/category-masthead-layout'
import { SITE_CONTAINER, SITE_MAIN_PAD_Y, SITE_PAGE_BG } from '@/lib/site-layout'
import { cn } from '@/lib/utils'

const bar = 'animate-pulse rounded-md bg-gray-200 dark:bg-slate-700'

function Shell({
  children,
  className,
  shellClassName,
}: {
  children: ReactNode
  className?: string
  shellClassName?: string
}) {
  return (
    <div
      className={cn(SITE_PAGE_BG, shellClassName)}
      aria-busy="true"
      aria-label="Sayfa yükleniyor"
    >
      <main className={cn(SITE_CONTAINER, SITE_MAIN_PAD_Y, className)}>{children}</main>
    </div>
  )
}

function PageHeaderSkeleton({
  withDescription = true,
}: {
  withDescription?: boolean
}) {
  return (
    <div className="mb-4 space-y-2 sm:mb-6">
      <div className={cn(bar, 'h-8 w-48 max-w-[70%]')} />
      {withDescription ? <div className={cn(bar, 'h-4 w-72 max-w-full')} /> : null}
    </div>
  )
}

function ShortsShelfSkeleton() {
  return (
    <div className="mb-6">
      <div className={cn(bar, 'mb-3 h-6 w-28')} />
      <div className="flex gap-2.5 overflow-hidden">
        {Array.from({ length: 6 }, (_, i) => (
          <div
            key={i}
            className={cn(bar, 'h-40 w-[7.25rem] shrink-0 rounded-xl sm:h-44 sm:w-32')}
          />
        ))}
      </div>
    </div>
  )
}

function WeatherMarketSkeleton() {
  return (
    <div className="relative mb-4 weather-market-bar">
      <div className="flex flex-row items-center gap-2 rounded-[5px] bg-gray-50 px-2 py-2.5 dark:bg-slate-900 sm:gap-3 sm:px-3 sm:py-3">
        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2.5">
          <div className={cn(bar, 'h-6 w-6 shrink-0 rounded-full sm:h-7 sm:w-7')} />
          <div className="min-w-0 space-y-1.5">
            <div className={cn(bar, 'h-4 w-14 sm:h-[1.125rem] sm:w-20')} />
            <div className={cn(bar, 'h-3 w-16')} />
          </div>
          <div className={cn(bar, 'h-3.5 w-3.5 shrink-0 rounded-sm')} />
        </div>
        <div className="h-8 w-px shrink-0 bg-gray-300 sm:h-10 dark:bg-slate-700" />
        <div className="flex min-w-0 flex-1 gap-2 overflow-hidden">
          {Array.from({ length: 6 }, (_, i) => (
            <div
              key={i}
              className={cn(bar, 'h-8 min-w-[4.25rem] flex-1 rounded-md sm:h-9')}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

/** /hava-durumu — gerçek sayfa düzeniyle birebir */
export function WeatherPageSkeleton() {
  return (
    <Shell>
      <div className="space-y-6" aria-busy="true" aria-label="Hava durumu yükleniyor">
        {/* breadcrumb */}
        <div className="flex items-center gap-1.5">
          <div className={cn(bar, 'h-3 w-16')} />
          <div className={cn(bar, 'h-3 w-2')} />
          <div className={cn(bar, 'h-3 w-20')} />
          <div className={cn(bar, 'h-3 w-2')} />
          <div className={cn(bar, 'h-3 w-24')} />
        </div>

        {/* title + search */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-2">
            <div className={cn(bar, 'h-8 w-56 max-w-[80%] sm:h-9 sm:w-72')} />
            <div className={cn(bar, 'h-3 w-40')} />
          </div>
          <div className={cn(bar, 'h-11 w-full sm:max-w-xs')} />
        </div>

        {/* tabs */}
        <div className="grid grid-cols-4 gap-1 rounded-[5px] border border-gray-200 bg-gray-50 p-1 dark:border-slate-700 dark:bg-slate-900/60">
          {Array.from({ length: 4 }, (_, i) => (
            <div
              key={i}
              className={cn(
                'h-9 rounded-md',
                i === 0
                  ? 'animate-pulse bg-white shadow-sm dark:bg-slate-950'
                  : bar,
              )}
            />
          ))}
        </div>

        {/* hero */}
        <section className="overflow-hidden rounded-[5px] border border-gray-200 bg-gradient-to-br from-sky-50 via-white to-amber-50 dark:border-slate-700 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900">
          <div className="grid gap-6 p-5 sm:grid-cols-[1.1fr_1fr] sm:p-7">
            <div className="flex items-start gap-4">
              <div className={cn(bar, 'h-20 w-20 shrink-0 rounded-full sm:h-24 sm:w-24')} />
              <div className="min-w-0 flex-1 space-y-2.5 pt-1">
                <div className={cn(bar, 'h-4 w-28')} />
                <div className={cn(bar, 'h-12 w-24 sm:h-14')} />
                <div className={cn(bar, 'h-3.5 w-48 max-w-full')} />
                <div className={cn(bar, 'h-3 w-40')} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {Array.from({ length: 9 }, (_, i) => (
                <div
                  key={i}
                  className="space-y-1.5 rounded-md bg-white/80 px-3 py-2 dark:bg-slate-900/70"
                >
                  <div className={cn(bar, 'h-2.5 w-14')} />
                  <div className={cn(bar, 'h-4 w-12')} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* hourly */}
        <section className="overflow-hidden rounded-[5px] border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900/40">
          <div className="border-b border-gray-100 px-4 py-3 dark:border-slate-800">
            <div className={cn(bar, 'mb-1.5 h-4 w-36')} />
            <div className={cn(bar, 'h-3 w-48')} />
          </div>
          <div className="flex overflow-hidden">
            {Array.from({ length: 10 }, (_, i) => (
              <div
                key={i}
                className="flex w-[72px] shrink-0 flex-col items-center gap-1.5 border-r border-gray-100 px-1.5 py-3 dark:border-slate-800"
              >
                <div className={cn(bar, 'h-3 w-8')} />
                <div className={cn(bar, 'h-8 w-8 rounded-full')} />
                <div className={cn(bar, 'h-4 w-7')} />
                <div className={cn(bar, 'h-2.5 w-6')} />
                <div className={cn(bar, 'h-2.5 w-8')} />
              </div>
            ))}
          </div>
        </section>

        {/* day rows */}
        <section className="overflow-hidden rounded-[5px] border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900/40">
          <div className="border-b border-gray-100 px-4 py-3 dark:border-slate-800">
            <div className={cn(bar, 'h-4 w-44')} />
          </div>
          <div>
            {Array.from({ length: 5 }, (_, i) => (
              <div
                key={i}
                className="flex items-center gap-3 border-b border-gray-100 px-3 py-3 last:border-0 sm:gap-4 sm:px-4 dark:border-slate-800"
              >
                <div className="min-w-[5.5rem] space-y-1.5 sm:min-w-[7.5rem]">
                  <div className={cn(bar, 'h-3.5 w-14')} />
                  <div className={cn(bar, 'h-2.5 w-20')} />
                </div>
                <div className={cn(bar, 'h-9 w-9 shrink-0 rounded-full sm:h-10 sm:w-10')} />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className={cn(bar, 'h-3.5 w-24')} />
                  <div className={cn(bar, 'h-2.5 w-40 max-w-full')} />
                </div>
                <div className={cn(bar, 'hidden h-1.5 w-16 rounded-full sm:block')} />
                <div className="shrink-0 space-y-1 text-right">
                  <div className={cn(bar, 'ml-auto h-4 w-8')} />
                  <div className={cn(bar, 'ml-auto h-3 w-6')} />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* popular cities */}
        <div>
          <div className={cn(bar, 'mb-3 h-3.5 w-24')} />
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className={cn(bar, 'h-8 w-20 rounded-md')} />
            ))}
          </div>
        </div>
      </div>
    </Shell>
  )
}

/** Ana sayfa — layout bilinmiyorsa CENTER_SIDES (loading.tsx HomeLoadingSkeleton kullanır) */
export function HomePageSkeleton({
  layout = MASTHEAD_LAYOUT.CENTER_SIDES,
}: {
  layout?: MastheadLayoutId
}) {
  return (
    <Shell>
      <MastheadLayoutSkeleton layout={layout} />
      <ShortsShelfSkeleton />
      <div className={cn(bar, 'mb-4 h-[90px] w-full rounded-[5px]')} aria-hidden />
      <WeatherMarketSkeleton />
      <div className="mt-6 space-y-3">
        <div className={cn(bar, 'h-7 w-36')} />
        <NewsSkeleton count={8} columns={4} plain showDate={false} />
      </div>
    </Shell>
  )
}

/** Kategori — layout bilinmiyorsa SLIDER_SIDEBAR; sıra gerçek sayfa ile aynı (ad → başlık) */
export function CategoryPageSkeleton({
  layout = MASTHEAD_LAYOUT.SLIDER_SIDEBAR,
  showMasthead = true,
}: {
  layout?: MastheadLayoutId
  showMasthead?: boolean
}) {
  return (
    <Shell>
      <div className={cn(bar, 'mb-4 h-[90px] w-full rounded-[5px]')} aria-hidden />
      <PageHeaderSkeleton withDescription={false} />
      {showMasthead ? <MastheadLayoutSkeleton layout={layout} /> : null}
      <NewsSkeleton count={8} columns={4} plain showDate={false} />
    </Shell>
  )
}

/** Etiket / video / galeri / popüler */
export function GridListPageSkeleton({
  count = 8,
  withFeatured = false,
  plain = false,
  showDate = true,
}: {
  count?: number
  withFeatured?: boolean
  plain?: boolean
  showDate?: boolean
}) {
  return (
    <Shell>
      {/* Çoğu liste sayfasında description yok — skeleton fazla yükseklik zıplatmasın */}
      <PageHeaderSkeleton withDescription={false} />
      {withFeatured ? (
        <div
          className={cn(
            bar,
            'mb-5 aspect-[16/10] w-full rounded-[5px] sm:aspect-[2/1] md:aspect-[21/9]',
          )}
        />
      ) : null}
      <NewsSkeleton count={count} columns={4} plain={plain} showDate={showDate} />
    </Shell>
  )
}

/** /arama landing — ortada ipucu kartı (grid yok) */
export function SearchLandingSkeleton() {
  return (
    <Shell>
      <div className="mx-auto max-w-2xl rounded-2xl border border-gray-200 bg-white px-6 py-12 dark:border-slate-700 dark:bg-slate-900">
        <div className={cn(bar, 'mx-auto mb-3 h-8 w-32')} />
        <div className={cn(bar, 'mx-auto mb-2 h-4 w-full max-w-md')} />
        <div className={cn(bar, 'mx-auto mb-6 h-4 w-3/4 max-w-sm')} />
        <div className="flex flex-wrap justify-center gap-2">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className={cn(bar, 'h-9 w-24 rounded-full')} />
          ))}
        </div>
      </div>
    </Shell>
  )
}

/** Arama sonuçları */
export function SearchPageSkeleton() {
  return <GridListPageSkeleton count={8} withFeatured />
}

export function ArticlePageSkeleton() {
  return (
    <Shell>
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,18rem)]">
        <div className="min-w-0 space-y-4">
          <div className={cn(bar, 'h-4 w-40')} />
          <div className={cn(bar, 'h-9 w-full max-w-3xl')} />
          <div className={cn(bar, 'h-5 w-2/3 max-w-xl')} />
          <div className="flex flex-wrap gap-3">
            <div className={cn(bar, 'h-4 w-28')} />
            <div className={cn(bar, 'h-4 w-24')} />
          </div>
          <div className={cn(bar, 'aspect-video w-full rounded-[5px]')} />
          <div className="space-y-2.5 pt-2">
            <div className={cn(bar, 'h-3.5 w-full')} />
            <div className={cn(bar, 'h-3.5 w-[95%]')} />
            <div className={cn(bar, 'h-3.5 w-[88%]')} />
            <div className={cn(bar, 'h-3.5 w-full')} />
            <div className={cn(bar, 'h-3.5 w-[70%]')} />
          </div>
        </div>
        <aside className="hidden space-y-3 lg:block">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="flex gap-3">
              <div className={cn(bar, 'h-16 w-24 shrink-0 rounded-md')} />
              <div className="flex-1 space-y-2 py-1">
                <div className={cn(bar, 'h-3 w-full')} />
                <div className={cn(bar, 'h-3 w-4/5')} />
              </div>
            </div>
          ))}
        </aside>
      </div>
    </Shell>
  )
}

/** Son dakika timeline — route + client ortak */
export function ListPageSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <Shell shellClassName="bg-gray-50 dark:bg-slate-950">
      <PageHeaderSkeleton />
      <div className="overflow-hidden rounded-lg border border-gray-100 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <SonDakikaRowsSkeleton rows={rows} />
      </div>
    </Shell>
  )
}

export function SonDakikaRowsSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="divide-y divide-gray-100 dark:divide-slate-800">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex gap-3 p-3 sm:gap-4 sm:p-4">
          <div className="hidden w-14 shrink-0 flex-col items-center pt-1 sm:flex">
            <div className={cn(bar, 'mb-1 h-2 w-2 rounded-full')} />
            <div className={cn(bar, 'h-4 w-10')} />
          </div>
          <div className={cn(bar, 'h-[4.25rem] w-24 shrink-0 rounded-md sm:h-20 sm:w-32 md:h-24 md:w-40')} />
          <div className="flex flex-1 flex-col justify-center gap-2">
            <div className={cn(bar, 'h-4 w-full')} />
            <div className={cn(bar, 'h-4 w-4/5')} />
            <div className={cn(bar, 'h-3 w-24')} />
          </div>
        </div>
      ))}
    </div>
  )
}

export function CommentListSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div
      className="divide-y divide-slate-100 dark:divide-slate-800/80"
      aria-busy="true"
      aria-label="Yorumlar yükleniyor"
    >
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="flex gap-3 py-4 sm:gap-4 sm:py-5">
          <div className={cn(bar, 'size-9 shrink-0 rounded-full sm:size-10')} />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <div className={cn(bar, 'h-3.5 w-24')} />
              <div className={cn(bar, 'h-3 w-16')} />
            </div>
            <div className={cn(bar, 'h-3.5 w-full')} />
            <div className={cn(bar, 'h-3.5 w-[85%]')} />
            <div className={cn(bar, 'h-3 w-20')} />
          </div>
        </div>
      ))}
    </div>
  )
}

export function ArticleBlockSkeleton() {
  return (
    <div
      className="space-y-4 rounded-xl border border-gray-100 bg-gray-50/80 p-4 dark:border-slate-800 dark:bg-slate-900/40"
      aria-busy="true"
    >
      <div className={cn(bar, 'h-8 max-w-md')} />
      <div className={cn(bar, 'h-56 w-full rounded-lg md:h-72')} />
      <div className="space-y-2">
        <div className={cn(bar, 'h-3 w-full')} />
        <div className={cn(bar, 'h-3 w-[92%]')} />
        <div className={cn(bar, 'h-3 w-[80%]')} />
      </div>
    </div>
  )
}
